import { makePayloadRequestInit } from '@/lib/api';
import type { OrgStore } from '.';
import { apiInvoke, shouldLoad } from '../helpers/load';
import type { APILoadParams, APIParams } from '../helpers/types';
import type { Campus } from '@/shared/types/campus';
import type { Ministry } from '@/shared/types/ministry';
import type { Entity } from '@/shared/types/entity';

const Path = {
  Campuses: '/campuses',
  Ministries: '/ministries',
  Entities: '/organizations',
};

export async function loadCampuses(
  { loaded, dispatch }: OrgStore,
  { api, force, auth }: APILoadParams,
) {
  if (!shouldLoad(loaded.campuses.current, force, auth)) {
    return;
  }

  loaded.campuses.current = true;
  const res = await apiInvoke<LoadCampusesResponse>(api, Path.Campuses, auth);
  dispatch({ type: 'setCampuses', data: res });
}

export async function loadMinistries(
  { loaded, dispatch }: OrgStore,
  { api, force, auth }: APILoadParams,
) {
  if (!shouldLoad(loaded.ministries.current, force, auth)) {
    return;
  }

  loaded.ministries.current = true;
  const res = await apiInvoke<LoadMinistriesResponse>(
    api,
    Path.Ministries,
    auth,
  );
  dispatch({ type: 'setMinistries', data: res });
}

export async function loadEntities(
  { loaded, dispatch }: OrgStore,
  { api, force, auth }: APILoadParams,
) {
  if (!shouldLoad(loaded.entities.current, force, auth)) {
    return;
  }

  loaded.entities.current = true;
  const res = await apiInvoke<LoadEntitiesResponse>(api, Path.Entities, auth);
  dispatch({ type: 'setEntities', data: res.data });
}

export async function updateCampus(
  { dispatch }: OrgStore,
  { api, auth, item }: APIParams & { item: Campus },
) {
  const url = `${Path.Campuses}/${item.id}`;
  const res = await apiInvoke<UpdateCampusResponse>(
    api,
    url,
    auth,
    makePayloadRequestInit(item.data, 'PATCH'),
  );
  dispatch({ type: 'setCampus', data: res });
}

export async function updateMinistry(
  { dispatch }: OrgStore,
  { api, auth, item }: APIParams & { item: Ministry },
) {
  const url = `${Path.Ministries}/${item.id}`;
  const res = await apiInvoke<UpdateMinistryResponse>(
    api,
    url,
    auth,
    makePayloadRequestInit(item.data, 'PATCH'),
  );
  dispatch({ type: 'setMinistry', data: res });
}

export async function createMinistry(
  { dispatch }: OrgStore,
  { api, auth, item }: APIParams & { item: Ministry },
) {
  const url = Path.Ministries;
  const res = await apiInvoke<CreateMinistryResponse>(
    api,
    url,
    auth,
    makePayloadRequestInit(item.data, 'POST'),
  );
  dispatch({ type: 'setMinistry', data: res });
}

type LoadCampusesResponse = Campus[];
type LoadEntitiesResponse = {
  data: Entity[];
};
type UpdateCampusResponse = Campus;
type LoadMinistriesResponse = Ministry[];
type CreateMinistryResponse = Ministry;
type UpdateMinistryResponse = Ministry;
